import { ChakraProvider, Theme, createSystem, defaultConfig } from "@chakra-ui/react"
import { AuthProvider, ColorModeProvider, useColorMode, ViewerProvider } from "@/contexts"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import "./App.css"

import { Router } from "./routes/Router"

const theme = createSystem(defaultConfig, {
  globalCss: {
    "html, body, *, *::before, *::after": {
      fontFamily: "body",
    },
  },
  theme: {
    tokens: {
      colors: {
        brand: {
          25: {
            value: "#5856e7"
          },
          50: {
            value: "#5856e7"
          },
          100: {
            value: "#324C82"
          },
          300: {
            value: "#2563EA"
          },
          500: {
            value: "#5856e7"
          },
          600: {
            value: "#5856e7"
          },
          900: {
            value: "#C6D0E3"
          },
        },
        darkBlue: {
          100: {
            value: "#0D1C46"
          },
          200: {
            value: "#1F2838"
          },
          300: {
            value: "#15160C"
          }
        },
        grant: {
          100: { value: "#E7EEFB" },
          200: { value: "#ECE9F0" },
          300: { value: "#FFF0E5" },
          400: { value: "#EBF3DE" },
        }
      },
      fonts: {
        body: { value: "Nunito Sans" },
        heading: { value: "Nunito Sans" },
      },
    },
    semanticTokens: {
      colors: {
        "page.bg": {
          value: { _light: "#ffffff", _dark: "#1a1a2e" },
        },
        "card.bg": {
          value: { _light: "#ffffff", _dark: "#16213e" },
        },
        "nav.bg": {
          value: { _light: "#ffffff", _dark: "#0f3460" },
        },
        "sidebar.bg": {
          value: { _light: "{colors.darkBlue.200}", _dark: "#0a0a1a" },
        },
        "border.default": {
          value: { _light: "{colors.gray.200}", _dark: "{colors.whiteAlpha.500}" },
        },
        "text.primary": {
          value: { _light: "{colors.black}", _dark: "{colors.gray.100}" },
        },
        "text.secondary": {
          value: { _light: "{colors.gray.600}", _dark: "{colors.gray.400}" },
        },
        "text.muted": {
          value: { _light: "{colors.gray.500}", _dark: "{colors.whiteAlpha.900}" },
        },
        "text.subtle": {
          value: { _light: "{colors.gray.400}", _dark: "{colors.whiteAlpha.600}" },
        },
        "row.red": {
          value: { _light: "{colors.red.50}", _dark: "#3b1a1a" },
        },
        "row.green": {
          value: { _light: "{colors.green.50}", _dark: "#1a2e1a" },
        },
        "row.blue": {
          value: { _light: "{colors.blue.50}", _dark: "#1a1a3b" },
        },
        "row.yellow": {
          value: { _light: "{colors.yellow.50}", _dark: "#3b3520" },
        },
        "border.red": {
          value: { _light: "{colors.red.200}", _dark: "{colors.red.700}" },
        },
        "border.green": {
          value: { _light: "{colors.green.200}", _dark: "{colors.green.700}" },
        },
        "border.yellow": {
          value: { _light: "{colors.yellow.400}", _dark: "{colors.yellow.700}" },
        },
        "table.header": {
          value: { _light: "{colors.gray.50}", _dark: "#1a2240" },
        },
        "table.section": {
          value: { _light: "{colors.gray.100}", _dark: "#1e2a4a" },
        },
        "table.border": {
          value: { _light: "{colors.gray.200}", _dark: "{colors.whiteAlpha.800}" },
        },
        "table.row": {
          value: { _light: "{colors.white}", _dark: "#16213e" },
        },
        "table.stripe": {
          value: { _light: "{colors.gray.50}", _dark: "#1a2744" },
        },
        "notes.bg": {
          value: { _light: "{colors.gray.50}", _dark: "#1a2240" },
        },
        "fab.bg": {
          value: { _light: "{colors.blackAlpha.700}", _dark: "{colors.whiteAlpha.700}" },
        },
        "fab.icon": {
          value: { _light: "white", _dark: "black" },
        },
        "hover.bg": {
          value: { _light: "{colors.gray.200}", _dark: "{colors.whiteAlpha.100}" },
        },
        "hover.blue": {
          value: { _light: "{colors.blue.100}", _dark: "#1a2250" },
        },
        "hover.red": {
          value: { _light: "{colors.red.100}", _dark: "#4a1a1a" },
        },
      },
    },
  },
})

const queryClient = new QueryClient()

const AppContent = () => {
  const { colorMode } = useColorMode()
  return (
    <Theme appearance={colorMode}>
      <Router />
    </Theme>
  )
}

function App() {
  return (
    <AuthProvider>
      <ViewerProvider>
        <ChakraProvider value={theme}>
          <ColorModeProvider>
            <QueryClientProvider client={queryClient}>
              <AppContent />
            </QueryClientProvider>
          </ColorModeProvider>
        </ChakraProvider>
      </ViewerProvider>
    </AuthProvider>
  )
}

export default App
